import React, { useState, useEffect } from "react";
import { useBusiness, api } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

// Placeholder bank transaction data - categorized from Plaid
// Generate placeholder data keys dynamically based on current date
const getCurrentMonthKey = (monthsAgo = 0) => {
  const date = new Date();
  date.setMonth(date.getMonth() - monthsAgo);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
};

const PLACEHOLDER_BANK_TRANSACTIONS = {
  [getCurrentMonthKey(0)]: {
    income: [
      { name: "Client Payment - ABC Corp", amount: 5500, category: "Service Revenue" },
      { name: "Client Payment - XYZ Inc", amount: 4200, category: "Service Revenue" },
      { name: "Consulting Fee", amount: 2800, category: "Consulting" },
    ],
    expenses: [
      { name: "Office Rent", amount: 1800, category: "Rent" },
      { name: "Software Subscriptions", amount: 450, category: "Software" },
      { name: "Internet & Phone", amount: 180, category: "Utilities" },
      { name: "Office Supplies", amount: 125, category: "Supplies" },
      { name: "Marketing - Google Ads", amount: 800, category: "Marketing" },
      { name: "Professional Services", amount: 500, category: "Professional Fees" },
    ]
  },
  [getCurrentMonthKey(1)]: {
    income: [
      { name: "Client Payment - ABC Corp", amount: 5500, category: "Service Revenue" },
      { name: "Client Payment - DEF LLC", amount: 6200, category: "Service Revenue" },
      { name: "Project Bonus", amount: 2500, category: "Bonus" },
    ],
    expenses: [
      { name: "Office Rent", amount: 1800, category: "Rent" },
      { name: "Software Subscriptions", amount: 450, category: "Software" },
      { name: "Internet & Phone", amount: 180, category: "Utilities" },
      { name: "Equipment Purchase", amount: 1200, category: "Equipment" },
      { name: "Marketing - Social Media", amount: 600, category: "Marketing" },
      { name: "Travel - Client Meeting", amount: 450, category: "Travel" },
    ]
  }
};

const ProfitLossCalculator = () => {
  const { selectedBusiness } = useBusiness();
  const [plRecords, setPlRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bankConnected, setBankConnected] = useState(false);
  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const [mode, setMode] = useState("manual"); // manual or bank
  const [period, setPeriod] = useState(`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`);
  const [incomeItems, setIncomeItems] = useState([{ name: "", amount: "" }]);
  const [expenseItems, setExpenseItems] = useState([{ name: "", amount: "" }]);

  useEffect(() => {
    if (selectedBusiness) {
      fetchPLRecords();
      checkBankConnection();
    }
  }, [selectedBusiness]);

  const checkBankConnection = async () => {
    try {
      const response = await api.get("/plaid/accounts");
      setBankConnected(response.data.length > 0);
    } catch (error) {
      setBankConnected(false);
    }
  };

  const fetchPLRecords = async () => {
    try {
      const response = await api.get(`/profit-loss/${selectedBusiness.id}`);
      setPlRecords(response.data);
    } catch (error) {
      console.error("Failed to fetch P&L records:", error);
    } finally {
      setLoading(false);
    }
  };

  // Get bank transactions for selected period
  const getBankData = () => {
    if (showPlaceholder && PLACEHOLDER_BANK_TRANSACTIONS[period]) {
      return PLACEHOLDER_BANK_TRANSACTIONS[period];
    }
    return { income: [], expenses: [] };
  };

  const bankData = getBankData();
  const bankIncome = bankData.income.reduce((sum, item) => sum + item.amount, 0);
  const bankExpenses = bankData.expenses.reduce((sum, item) => sum + item.amount, 0);
  const bankProfit = bankIncome - bankExpenses;

  const addIncomeItem = () => {
    setIncomeItems([...incomeItems, { name: "", amount: "" }]);
  };

  const addExpenseItem = () => {
    setExpenseItems([...expenseItems, { name: "", amount: "" }]);
  };

  const updateIncomeItem = (index, field, value) => {
    const updated = [...incomeItems];
    updated[index] = { ...updated[index], [field]: value };
    setIncomeItems(updated);
  };

  const updateExpenseItem = (index, field, value) => {
    const updated = [...expenseItems];
    updated[index] = { ...updated[index], [field]: value };
    setExpenseItems(updated);
  };

  const removeIncomeItem = (index) => {
    if (incomeItems.length > 1) {
      setIncomeItems(incomeItems.filter((_, i) => i !== index));
    }
  };

  const removeExpenseItem = (index) => {
    if (expenseItems.length > 1) {
      setExpenseItems(expenseItems.filter((_, i) => i !== index));
    }
  };

  const calculateTotals = () => {
    const totalIncome = incomeItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const totalExpenses = expenseItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    return {
      totalIncome,
      totalExpenses,
      netProfit: totalIncome - totalExpenses,
    };
  };

  const importBankData = () => {
    if (bankData.income.length > 0) {
      setIncomeItems(bankData.income.map(item => ({ name: item.name, amount: item.amount.toString() })));
    }
    if (bankData.expenses.length > 0) {
      setExpenseItems(bankData.expenses.map(item => ({ name: item.name, amount: item.amount.toString() })));
    }
    toast.success("Bank transactions imported! Review and save.");
  };

  const savePL = async () => {
    setSaving(true);
    try {
      const validIncomeItems = incomeItems
        .filter(item => item.name && item.amount)
        .map(item => ({ name: item.name, amount: parseFloat(item.amount) }));
      
      const validExpenseItems = expenseItems
        .filter(item => item.name && item.amount)
        .map(item => ({ name: item.name, amount: parseFloat(item.amount) }));

      await api.post("/profit-loss", {
        business_id: selectedBusiness.id,
        period: period,
        income_items: validIncomeItems,
        expense_items: validExpenseItems,
      });
      
      toast.success("Profit & Loss statement saved!");
      fetchPLRecords();
      setIncomeItems([{ name: "", amount: "" }]);
      setExpenseItems([{ name: "", amount: "" }]);
    } catch (error) {
      toast.error("Failed to save P&L statement");
    } finally {
      setSaving(false);
    }
  };

  if (!selectedBusiness) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Please select or create a business first</p>
      </div>
    );
  }

  const totals = calculateTotals();
  const periodOptions = [];
  const currentDate = new Date();
  for (let i = 0; i < 12; i++) {
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
    periodOptions.push({
      value: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`,
      label: date.toLocaleDateString("en-US", { month: "long", year: "numeric" }),
    });
  }

  return (
    <div className="space-y-8" data-testid="profit-loss-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-stone-900">Profit & Loss</h1>
          <p className="text-stone-600">Track income and expenses - manually or from bank</p>
        </div>
        <div className="flex items-center gap-3">
          {!bankConnected && (
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
              <span className="mr-1">⚠️</span> Bank not connected
            </Badge>
          )}
          <Button
            variant="outline"
            onClick={() => setShowPlaceholder(!showPlaceholder)}
            className="text-sm"
          >
            {showPlaceholder ? "Hide" : "Show"} Sample Data
          </Button>
        </div>
      </div>

      {/* Bank Connection Banner */}
      {!bankConnected && (
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="p-3 rounded-full bg-blue-100">
                <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                  <path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v3M12 14v3M16 14v3" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900">Auto-Import Transactions</h3>
                <p className="text-sm text-blue-700">
                  Connect your bank to automatically categorize income and expenses.
                  {showPlaceholder && " Sample data shown for demonstration."}
                </p>
              </div>
              <Button 
                onClick={() => window.location.href = '/bank-accounts'}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Connect Bank
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Calculator Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Period Selection */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Period</CardTitle>
                {showPlaceholder && PLACEHOLDER_BANK_TRANSACTIONS[period] && (
                  <Button
                    onClick={importBankData}
                    variant="outline"
                    size="sm"
                    className="text-blue-600 border-blue-200 hover:bg-blue-50"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                      <path d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                    </svg>
                    Import from Bank
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-64" data-testid="period-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {periodOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Bank Preview - Show when placeholder data exists */}
          {showPlaceholder && PLACEHOLDER_BANK_TRANSACTIONS[period] && (
            <Card className="bg-blue-50/50 border-blue-200">
              <CardHeader>
                <CardTitle className="text-blue-800 text-lg flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                    <path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v3M12 14v3M16 14v3" />
                  </svg>
                  Bank Transactions Preview
                </CardTitle>
                <CardDescription className="text-blue-600">
                  Automatically categorized from your connected bank account
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-[#1e3a5f] mb-2">Income ({bankData.income.length})</h4>
                    <div className="space-y-2">
                      {bankData.income.map((item) => (
                        <div key={item.name} className="flex justify-between text-sm p-2 bg-white rounded border border-[#1e3a5f]/15">
                          <span className="text-stone-700">{item.name}</span>
                          <span className="font-medium text-[#1e3a5f]">${item.amount.toLocaleString()}</span>
                        </div>
                      ))}
                      <div className="flex justify-between font-medium pt-2 border-t border-[#1e3a5f]/20">
                        <span>Total</span>
                        <span className="text-[#1e3a5f]">${bankIncome.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-amber-700 mb-2">Expenses ({bankData.expenses.length})</h4>
                    <div className="space-y-2">
                      {bankData.expenses.map((item, i) => (
                        <div key={i} className="flex justify-between text-sm p-2 bg-white rounded border border-amber-100">
                          <span className="text-stone-700">{item.name}</span>
                          <span className="font-medium text-amber-600">${item.amount.toLocaleString()}</span>
                        </div>
                      ))}
                      <div className="flex justify-between font-medium pt-2 border-t border-amber-200">
                        <span>Total</span>
                        <span className="text-amber-600">${bankExpenses.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-4 p-3 rounded-lg bg-white border border-blue-200 flex justify-between items-center">
                  <span className="font-medium text-stone-700">Net Profit from Bank Data</span>
                  <span className={`text-xl font-bold ${bankProfit >= 0 ? 'text-[#1e3a5f]' : 'text-red-600'}`}>
                    ${bankProfit.toLocaleString()}
                  </span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Income Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-[#1e3a5f]">Income</CardTitle>
                  <CardDescription>Add all income sources for this period</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={addIncomeItem} data-testid="add-income-btn">
                  + Add Item
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {incomeItems.map((item, index) => (
                <div key={index} className="flex gap-4 items-end">
                  <div className="flex-1">
                    <Label className="text-xs">Description</Label>
                    <Input
                      placeholder="e.g., Service Revenue"
                      value={item.name}
                      onChange={(e) => updateIncomeItem(index, "name", e.target.value)}
                      data-testid={`income-name-${index}`}
                    />
                  </div>
                  <div className="w-40">
                    <Label className="text-xs">Amount</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400">$</span>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        value={item.amount}
                        onChange={(e) => updateIncomeItem(index, "amount", e.target.value)}
                        className="pl-7"
                        data-testid={`income-amount-${index}`}
                      />
                    </div>
                  </div>
                  {incomeItems.length > 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeIncomeItem(index)}
                      className="text-stone-400 hover:text-red-500"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </Button>
                  )}
                </div>
              ))}
              <div className="pt-4 border-t flex justify-between">
                <span className="font-medium">Total Income</span>
                <span className="font-bold text-[#1e3a5f]" data-testid="total-income">
                  ${totals.totalIncome.toLocaleString()}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Expenses Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-amber-600">Expenses</CardTitle>
                  <CardDescription>Add all expenses for this period</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={addExpenseItem} data-testid="add-expense-btn">
                  + Add Item
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {expenseItems.map((item, index) => (
                <div key={index} className="flex gap-4 items-end">
                  <div className="flex-1">
                    <Label className="text-xs">Description</Label>
                    <Input
                      placeholder="e.g., Office Supplies"
                      value={item.name}
                      onChange={(e) => updateExpenseItem(index, "name", e.target.value)}
                      data-testid={`expense-name-${index}`}
                    />
                  </div>
                  <div className="w-40">
                    <Label className="text-xs">Amount</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400">$</span>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        value={item.amount}
                        onChange={(e) => updateExpenseItem(index, "amount", e.target.value)}
                        className="pl-7"
                        data-testid={`expense-amount-${index}`}
                      />
                    </div>
                  </div>
                  {expenseItems.length > 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeExpenseItem(index)}
                      className="text-stone-400 hover:text-red-500"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </Button>
                  )}
                </div>
              ))}
              <div className="pt-4 border-t flex justify-between">
                <span className="font-medium">Total Expenses</span>
                <span className="font-bold text-amber-600" data-testid="total-expenses">
                  ${totals.totalExpenses.toLocaleString()}
                </span>
              </div>
            </CardContent>
          </Card>

          <Button
            onClick={savePL}
            disabled={saving}
            className="w-full bg-[#1e3a5f] hover:bg-[#2d5a8e] h-12"
            data-testid="save-pl-btn"
          >
            {saving ? "Saving..." : "Save Profit & Loss Statement"}
          </Button>
        </div>

        {/* Summary */}
        <div className="space-y-6">
          <Card className={`${totals.netProfit >= 0 ? 'bg-[#1e3a5f]/5 border-[#1e3a5f]/20' : 'bg-red-50 border-red-200'}`}>
            <CardHeader>
              <CardTitle>Net Profit</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-4">
                <p className={`text-5xl font-bold ${totals.netProfit >= 0 ? 'text-[#1e3a5f]' : 'text-red-600'}`} data-testid="net-profit">
                  ${totals.netProfit.toLocaleString()}
                </p>
                <p className="text-stone-600 mt-2">
                  {totals.netProfit >= 0 ? "Profit" : "Loss"}
                </p>
              </div>
              <div className="space-y-2 mt-4">
                <div className="flex justify-between text-sm">
                  <span>Total Income</span>
                  <span className="text-[#1e3a5f]">${totals.totalIncome.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Total Expenses</span>
                  <span className="text-amber-600">-${totals.totalExpenses.toLocaleString()}</span>
                </div>
                <div className="border-t pt-2 flex justify-between font-medium">
                  <span>Net</span>
                  <span className={totals.netProfit >= 0 ? 'text-[#1e3a5f]' : 'text-red-600'}>
                    ${totals.netProfit.toLocaleString()}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Bank vs Manual Comparison */}
          {showPlaceholder && PLACEHOLDER_BANK_TRANSACTIONS[period] && totals.totalIncome > 0 && (
            <Card className="bg-blue-50 border-blue-200">
              <CardHeader>
                <CardTitle className="text-blue-800 text-sm">Bank vs Manual Entry</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-blue-700">Bank Income:</span>
                  <span className="font-medium">${bankIncome.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-blue-700">Your Entry:</span>
                  <span className="font-medium">${totals.totalIncome.toLocaleString()}</span>
                </div>
                {bankIncome !== totals.totalIncome && (
                  <p className="text-xs text-amber-600 pt-2">
                    ⚠️ Your entries differ from bank data. Review for accuracy.
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Past Records */}
          <Card>
            <CardHeader>
              <CardTitle>Past Statements</CardTitle>
            </CardHeader>
            <CardContent>
              {plRecords.length > 0 ? (
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {plRecords.map(record => (
                    <div key={record.id} className="p-3 rounded-lg bg-stone-50 border border-stone-100">
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-sm">{record.period}</span>
                        <span className={`font-bold ${record.net_profit >= 0 ? 'text-[#1e3a5f]' : 'text-red-600'}`}>
                          ${record.net_profit.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs text-stone-500 mt-1">
                        <span>Income: ${record.total_income.toLocaleString()}</span>
                        <span>Exp: ${record.total_expenses.toLocaleString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-sm text-muted-foreground py-4">
                  No past statements yet
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ProfitLossCalculator;
